"use client";

import { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment,
  LinearProgress, MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableHead, TableRow, TablePagination, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon         from "@mui/icons-material/Add";
import SearchIcon      from "@mui/icons-material/Search";
import VisibilityIcon  from "@mui/icons-material/Visibility";
import CancelIcon      from "@mui/icons-material/Cancel";
import DownloadIcon    from "@mui/icons-material/Download";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import Swal from "sweetalert2";

import { axiosRequest }      from "../../utility/axios";
import { apiBaseUrl }        from "../../constant/endpoint";
import { useSelector }       from "react-redux";
import { getSweetAlert }     from "../../helper/index.js";
import CreateLoanDrawer      from "./loanForm.js";

/* ── helpers ─────────────────────────────────────────────────────── */
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const loanDisplayStatus = (loan) => {
  const outstanding = Number(loan.total_disbursed || 0) - Number(loan.total_paid || 0);
  switch (loan.reqStatus) {
    case "UNDER_REVIEW": return { label: "Pending",  color: "warning" };
    case "REJECTED":     return { label: "Rejected", color: "error"   };
    case "CLOSED":       return { label: "Closed",   color: "default" };
    case "APPROVED":
      if (!loan.total_disbursed) return { label: "Approved",    color: "info"    };
      if (outstanding > 0)       return { label: "Active",      color: "primary" };
      return                            { label: "Fully Repaid", color: "success" };
    default: return { label: loan.reqStatus, color: "default" };
  }
};

const APPROVAL_COLOR = { PENDING: "warning", APPROVED: "success", REJECTED: "error" };

/* ── component ───────────────────────────────────────────────────── */
export default function LoanManager() {
  const token = useSelector((s) => s.loginSlice.token);

  /* list state */
  const [loans,      setLoans]      = useState([]);
  const [totals,     setTotals]     = useState({});
  const [loading,    setLoading]    = useState(false);
  const [page,       setPage]       = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows,  setTotalRows]  = useState(0);

  /* filters */
  const [filterStatus,  setFilterStatus]  = useState("");
  const [filterPlayer,  setFilterPlayer]  = useState("");
  const [filterInput,   setFilterInput]   = useState("");
  const [dateFrom,      setDateFrom]      = useState("");
  const [dateTo,        setDateTo]        = useState("");

  const [exporting, setExporting] = useState(false);

  /* drawers / dialogs */
  const [createOpen,   setCreateOpen]   = useState(false);
  const [detailLoan,   setDetailLoan]   = useState(null);
  const [detailOpen,   setDetailOpen]   = useState(false);
  const [detailData,   setDetailData]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* disburse dialog */
  const [disburseOpen,    setDisburseOpen]    = useState(false);
  const [disburseLoan,    setDisburseLoan]    = useState(null);
  const [disburseAmount,  setDisburseAmount]  = useState("");
  const [disburseError,   setDisburseError]   = useState("");
  const [disburseLoading, setDisburseLoading] = useState(false);

  /* ── fetch list ─────────────────────────────────────────────── */
  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page:  page + 1,
        size:  rowsPerPage,
        ...(filterStatus && { reqStatus: filterStatus }),
        ...(filterPlayer  && { playerId:  filterPlayer  }),
        ...(dateFrom      && { date_from: dateFrom }),
        ...(dateTo        && { date_to:   dateTo   }),
      });
      const res = await axiosRequest("GET", `${apiBaseUrl}/loans?${params}`, token);
      if (res?.data?.status) {
        setLoans(res.data.loanList || []);
        setTotals(res.data.totals  || {});
        setTotalRows(res.data.pagniationData?.totalRecords || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, filterStatus, filterPlayer, dateFrom, dateTo]);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  /* ── detail ─────────────────────────────────────────────────── */
  const openDetail = async (loan) => {
    setDetailLoan(loan);
    setDetailOpen(true);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/loan/${loan._id}`, token);
      if (res?.data?.status) setDetailData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  /* ── disburse ───────────────────────────────────────────────── */
  const openDisburse = (loan) => {
    const outstanding = Number(loan.total_disbursed || 0) - Number(loan.total_paid || 0);
    const available   = Math.max(0, Number(loan.amount) - outstanding);
    setDisburseLoan({ ...loan, _available: available });
    setDisburseAmount("");
    setDisburseError("");
    setDisburseOpen(true);
  };

  const handleDisburseAmountChange = (val) => {
    setDisburseAmount(val);
    const num = Number(val);
    if (!val || num <= 0) {
      setDisburseError("Enter a valid amount");
    } else if (num > (disburseLoan?._available || 0)) {
      setDisburseError(`Max available: ${fmt(disburseLoan?._available)}`);
    } else {
      setDisburseError("");
    }
  };

  const handleDisburseSubmit = async () => {
    if (!disburseAmount || disburseError) return;
    setDisburseLoading(true);
    try {
      const res = await axiosRequest(
        "PATCH",
        `${apiBaseUrl}/loan/${disburseLoan._id}/admin-approve`,
        token,
        { amount: Number(disburseAmount) }
      );
      if (res?.data?.status) {
        getSweetAlert("success", res.data.message || "Disbursed successfully");
        setDisburseOpen(false);
        fetchLoans();
      } else {
        getSweetAlert("error", res?.data?.message || "Failed to disburse");
      }
    } catch (err) {
      console.error(err);
      getSweetAlert("error", "Something went wrong");
    } finally {
      setDisburseLoading(false);
    }
  };

  /* ── reject ─────────────────────────────────────────────────── */
  const handleReject = async (loan) => {
    const result = await Swal.fire({
      title: "Reject Loan?",
      text:  `Reject loan for player ${loan.playerId}?`,
      icon:  "warning",
      showCancelButton:  true,
      confirmButtonText: "Yes, Reject",
      confirmButtonColor: "#d32f2f",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosRequest("PATCH", `${apiBaseUrl}/loan/${loan._id}/admin-reject`, token);
      if (res?.data?.status) {
        getSweetAlert("success", "Loan rejected");
        fetchLoans();
      } else {
        getSweetAlert("error", res?.data?.message || "Failed to reject");
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* ── search ─────────────────────────────────────────────────── */
  const applyPlayerFilter = () => {
    setFilterPlayer(filterInput.trim());
    setPage(0);
  };

  const clearFilters = () => {
    setFilterStatus("");
    setFilterPlayer("");
    setFilterInput("");
    setDateFrom("");
    setDateTo("");
    setPage(0);
  };

  /* ── export CSV ─────────────────────────────────────────────── */
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        ...(filterStatus && { reqStatus: filterStatus }),
        ...(filterPlayer  && { playerId:  filterPlayer  }),
        ...(dateFrom      && { date_from: dateFrom }),
        ...(dateTo        && { date_to:   dateTo   }),
      });
      const res = await fetch(`${apiBaseUrl}/loans/export-csv?${params}`, {
        method: "GET",
        headers: { token },
      });
      if (!res.ok) { console.error("Export failed", res.status); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `loans_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
    } finally {
      setExporting(false);
    }
  };

  /* ── summary cards ───────────────────────────────────────────── */
  const netOutstanding = Number(totals.totalDisbursed || 0) - Number(totals.totalPaid || 0);

  const SummaryCard = ({ label, value, color = "text.primary" }) => (
    <Paper elevation={2} sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700} color={color}>{fmt(value)}</Typography>
    </Paper>
  );

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" } }}>
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Loan Manager</Typography>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
<Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create Loan
          </Button>
        </Box>
      </Box>

      {/* Summary */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Total Credit Limit"   value={totals.totalCreditLimit} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Total Disbursed"      value={totals.totalDisbursed} color="primary.main" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Total Repaid"         value={totals.totalPaid}      color="success.main" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard label="Net Outstanding"      value={netOutstanding}        color={netOutstanding > 0 ? "error.main" : "text.primary"} />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Box display="flex" gap={1}>
              <TextField
                size="small" fullWidth label="Search Player ID"
                value={filterInput}
                onChange={(e) => setFilterInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyPlayerFilter()}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <Button variant="contained" size="small" onClick={applyPlayerFilter} sx={{ minWidth: 70 }}>
                Search
              </Button>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2}>
            <Select
              size="small" fullWidth displayEmpty
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="UNDER_REVIEW">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved / Active</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              size="small" fullWidth type="date" label="From"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <TextField
              size="small" fullWidth type="date" label="To"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <Button variant="outlined" size="small" fullWidth onClick={clearFilters}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "grey.100" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Credit Limit</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Disbursed</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Paid</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} width={160}>Outstanding</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Available</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No loans found
                    </TableCell>
                  </TableRow>
                ) : loans.map((loan) => {
                  const outstanding = Number(loan.total_disbursed || 0) - Number(loan.total_paid || 0);
                  const available   = Math.max(0, Number(loan.amount) - outstanding);
                  const pct         = loan.amount ? Math.min(100, (outstanding / Number(loan.amount)) * 100) : 0;
                  const { label, color } = loanDisplayStatus(loan);

                  return (
                    <TableRow key={loan._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {loan.user?.name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {loan.playerId}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {fmt(loan.amount)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2">
                          {fmt(loan.total_disbursed)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {fmt(loan.total_paid)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600}
                          color={outstanding > 0 ? "error.main" : "text.secondary"}>
                          {fmt(outstanding)}
                        </Typography>
                        {loan.total_disbursed > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              mt: 0.5, height: 5, borderRadius: 3,
                              backgroundColor: "grey.200",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: pct > 75 ? "#d32f2f" : pct > 40 ? "#f57c00" : "#1976d2",
                              },
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {fmt(available)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Chip label={label} color={color} size="small" />
                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => openDetail(loan)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {loan.reqStatus === "APPROVED" && available > 0 && (
                            <Tooltip title="Disburse">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openDisburse(loan)}
                              >
                                <AccountBalanceWalletIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {["UNDER_REVIEW", "APPROVED"].includes(loan.reqStatus) && (
                            <Tooltip title="Reject Loan">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReject(loan)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>

      {/* ── Create Loan Drawer ─────────────────────────────────── */}
      <CreateLoanDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        refresh={fetchLoans}
      />

      {/* ── Disburse Dialog ────────────────────────────────────── */}
      <Dialog open={disburseOpen} onClose={() => setDisburseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Disburse Loan
          <Typography variant="body2" color="text.secondary">
            Player: {disburseLoan?.playerId}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {disburseLoan && (
            <Grid container spacing={1.5} mt={0.5}>
              {[
                { label: "Credit Limit",     value: `${fmt(disburseLoan.amount)}` },
                { label: "Total Disbursed",  value: `${fmt(disburseLoan.total_disbursed)}` },
                { label: "Total Repaid",     value: `${fmt(disburseLoan.total_paid)}` },
                { label: "Available",        value: `${fmt(disburseLoan._available)}`, highlight: true },
              ].map(({ label, value, highlight }) => (
                <Grid item xs={6} key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography fontWeight={700} color={highlight ? "success.main" : "text.primary"}>
                    {value}
                  </Typography>
                </Grid>
              ))}

              <Grid item xs={12} mt={1}>
                <TextField
                  fullWidth size="small" label="Amount to Disburse ()"
                  type="number"
                  value={disburseAmount}
                  onChange={(e) => handleDisburseAmountChange(e.target.value)}
                  error={!!disburseError}
                  helperText={disburseError || `Max: ${fmt(disburseLoan._available)}`}
                  inputProps={{ min: 1, max: disburseLoan._available }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setDisburseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDisburseSubmit}
            disabled={disburseLoading || !disburseAmount || !!disburseError}
          >
            {disburseLoading ? "Disbursing..." : "Disburse"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Dialog ──────────────────────────────────────── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" fontWeight={700}>Loan Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {detailData?.loan?.user?.name || detailLoan?.user?.name || "—"} &nbsp;·&nbsp;
                {detailLoan?.playerId}
              </Typography>
            </Box>
            {detailLoan && (
              <Chip
                label={loanDisplayStatus(detailData?.loan || detailLoan).label}
                color={loanDisplayStatus(detailData?.loan || detailLoan).color}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <Divider />

        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : detailData ? (() => {
            const loan        = detailData.loan;
            const outstanding = detailData.outstanding || 0;
            const available   = detailData.available   || 0;

            return (
              <>
                {/* Summary row */}
                <Grid container spacing={2} mb={3} mt={0.5}>
                  {[
                    { label: "Credit Limit",    value: `${fmt(loan.amount)}` },
                    { label: "Total Disbursed", value: `${fmt(loan.total_disbursed)}` },
                    { label: "Total Paid",      value: `${fmt(loan.total_paid)}`, color: "success.main" },
                    { label: "Outstanding",     value: `${fmt(outstanding)}`,     color: outstanding > 0 ? "error.main" : "text.primary" },
                    { label: "Available",       value: `${fmt(available)}`,       color: "success.main" },
                  ].map(({ label, value, color }) => (
                    <Grid item xs={6} sm={4} key={label}>
                      <Paper elevation={0} sx={{ p: 1.5, backgroundColor: "grey.50", borderRadius: 2, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography fontWeight={700} color={color || "text.primary"}>{value}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* Approvals */}
                <Typography fontWeight={700} mb={1}>Approval Status</Typography>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", mb: 3 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "grey.100" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Approver Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Action Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(loan.approvals || []).map((a, i) => (
                        <TableRow key={i}>
                          <TableCell>{a.approverEmail}</TableCell>
                          <TableCell align="center">
                            <Chip label={a.status} color={APPROVAL_COLOR[a.status] || "default"} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            {a.approvedAt ? new Date(a.approvedAt).toLocaleString("en-IN") : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {/* Disbursements */}
                {(loan.disbursements || []).length > 0 && (
                  <>
                    <Typography fontWeight={700} mb={1}>Disbursements</Typography>
                    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", mb: 3 }}>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: "grey.100" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loan.disbursements.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: "primary.main" }}>
                                {fmt(d.amount)}
                              </TableCell>
                              <TableCell align="right">
                                {new Date(d.date).toLocaleString("en-IN")}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: "grey.50" }}>
                            <TableCell colSpan={1} sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                              {fmt(loan.total_disbursed)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </>
                )}

                {/* Payments */}
                {(loan.payments || []).length > 0 && (
                  <>
                    <Typography fontWeight={700} mb={1}>Repayment History</Typography>
                    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: "grey.100" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Ref</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loan.payments.map((p, i) => (
                            <TableRow key={i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>
                                {fmt(p.amount)}
                              </TableCell>
                              <TableCell align="right">
                                {new Date(p.date).toLocaleString("en-IN")}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {p.txn_ref || "—"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: "grey.50" }}>
                            <TableCell colSpan={1} sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>
                              {fmt(loan.total_paid)}
                            </TableCell>
                            <TableCell colSpan={2} />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </>
                )}

                {(loan.disbursements || []).length === 0 && (loan.payments || []).length === 0 && (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    No disbursements or payments yet
                  </Typography>
                )}
              </>
            );
          })() : (
            <Typography color="text.secondary" textAlign="center" py={2}>
              Failed to load details
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
          {detailData?.loan?.reqStatus === "APPROVED" && detailData?.available > 0 && (
            <Button
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
              onClick={() => {
                setDetailOpen(false);
                openDisburse(detailData.loan);
              }}
            >
              Disburse
            </Button>
          )}
        </DialogActions>
      </Dialog>
</Box>
    </Box>
  );
}
